import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';

import { errorText } from '../lib/errorText';
import { STROKE, ACCENT, STATE, TEXT } from '../theme/tokens';

// Page-level helpers every routed page shares (FD-457, promoted in FD-466).

/**
 * One load/error/empty treatment for every page.
 *
 * ERRORS SAY WHAT THE SERVER SAID, through the SHARED `errorText` helper rather
 * than a local copy of it: an `ApiError` already carries the ADR-007 envelope's
 * `error.message`, which is written for an operator. Swallowing it into
 * "Something went wrong" is how a 403 FORBIDDEN_TENANT becomes an hour of
 * guessing.
 */
export function QueryGate({
  isLoading,
  error,
  children,
  label,
}: {
  isLoading: boolean;
  error: unknown;
  children: ReactNode;
  label: string;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-1 py-12">
        <Spinner size="tiny" />
        <span className="text-[13px]" style={{ color: TEXT.secondary }}>
          Loading {label}…
        </span>
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="rounded-lg border px-5 py-4"
        style={{ borderColor: STATE.critical.glyph, backgroundColor: STATE.critical.soft }}
      >
        <p className="m-0 text-[13px] font-semibold" style={{ color: STATE.critical.text }}>
          Could not load {label}
        </p>
        <p className="mt-1 text-[12px]" style={{ color: STATE.critical.text }}>
          {errorText(error)}
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

/** The tab strip used by every detail page. Tabs are routes. */
export function TabStrip({
  base,
  current,
  tabs,
}: {
  /** e.g. `/migrations/<id>` */
  base: string;
  current: string;
  tabs: { id: string; label: string; count?: number }[];
}) {
  return (
    <div className="mb-5 flex gap-1 border-b" style={{ borderColor: STROKE.base }}>
      {tabs.map((tab) => {
        const active = tab.id === current;
        return (
          <Link
            key={tab.id}
            to={`${base}/${tab.id}`}
            className="relative px-3.5 py-2.5 text-[13px] no-underline"
            // The active LABEL takes `pressed` (6.61:1) and the indicator bar
            // below takes `base`. That split is the token module's rule — text
            // rung vs graphical rung — and this is the one component where both
            // rungs appear within a few pixels of each other, so getting it
            // wrong here is the most visible way to get it wrong.
            style={{
              color: active ? ACCENT.pressed : TEXT.secondary,
              fontWeight: active ? 600 : 400,
            }}
          >
            {tab.label}
            {typeof tab.count === 'number' && (
              <span style={{ color: TEXT.secondary, fontWeight: 400 }}> ({tab.count})</span>
            )}
            {active && (
              <span
                aria-hidden="true"
                className="absolute inset-x-1 -bottom-px h-[2px] rounded-t"
                style={{ backgroundColor: ACCENT.base }}
              />
            )}
          </Link>
        );
      })}
    </div>
  );
}

/** The `:tab` route param, with a default. */
export function useTab(fallback: string): string {
  const { tab } = useParams<{ tab: string }>();
  return tab ?? fallback;
}

/** A back link to the surface a detail page was opened from. */
export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className="mb-3 cursor-pointer border-0 bg-transparent p-0 text-[12px]"
      style={{ color: ACCENT.pressed }}
    >
      ← {children}
    </button>
  );
}
